import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { OrderSummary } from '../models/Order';
import OrderUtils from '../utils/orderUtils';
import DateUtils from '../utils/dateUtils';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  orderSummaries: OrderSummary[];
  loading?: boolean;
  preparing?: boolean;
  expectedCount?: number;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  orderSummaries,
  loading = false,
  preparing = false,
  expectedCount
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const isConfirmationValid = confirmationText.toUpperCase() === 'SUPPRIMER';
  const orderCount = (preparing && typeof expectedCount === 'number') ? expectedCount : orderSummaries.length;

  const handleConfirm = async () => {
    if (!isConfirmationValid) return;

    setIsConfirming(true);
    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    setIsConfirming(false);
    onClose();
  };

  const getTotalAmount = () => {
    return orderSummaries.reduce((sum, order) => sum + order.totalAmount, 0);
  };

  const getRecentOrdersCount = () => {
    return orderSummaries.filter(order => 
      OrderUtils.isRecentOrder(order.createdAt)
    ).length;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        backgroundColor: '#fff3e0',
        color: '#e65100'
      }}>
        <WarningIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" component="div">
            Confirmation de suppression
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Les ordres seront archivés puis supprimés de la liste active.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Résumé de l'action */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Vous êtes sur le point de supprimer <strong>{orderCount}</strong> ordre(s).
          </Typography>
          <Typography variant="body2">
            Montant total concerné : <strong>{OrderUtils.formatAmount(getTotalAmount())}</strong>
          </Typography>
          {getRecentOrdersCount() > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              ⚠️ <strong>{getRecentOrdersCount()}</strong> ordre(s) récent(s) (moins d'1 heure)
            </Typography>
          )}
        </Alert>

        {/* Liste des ordres à supprimer */}
        <Typography variant="h6" gutterBottom>
          Ordres concernés :
        </Typography>

        {preparing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Chargement des ordres sélectionnés...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            maxHeight: 300, 
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 3
          }}>
            <List dense>
              {orderSummaries.map((order, index) => (
                <ListItem 
                  key={order.id}
                  sx={{ 
                    borderBottom: index < orderSummaries.length - 1 ? '1px solid #f0f0f0' : 'none',
                    py: 1.5
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          #{order.id}
                        </Typography>
                        <Chip 
                          label={OrderUtils.getStatusLabel(order.status)}
                          size="small"
                          sx={{ 
                            backgroundColor: OrderUtils.getStatusColor(order.status),
                            color: 'white',
                            fontSize: '0.75rem'
                          }}
                        />
                        {OrderUtils.isRecentOrder(order.createdAt) && (
                          <Chip 
                            label="Récent"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box component="div">
                        <Typography component="div" variant="body2" color="text.secondary">
                          {order.clientName} - {OrderUtils.formatAmount(order.totalAmount)}
                        </Typography>
                        <Typography component="div" variant="caption" color="text.secondary">
                          {DateUtils.formatDateTimeFR(order.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Champ de confirmation */}
        <Typography variant="body1" gutterBottom sx={{ fontWeight: 'medium' }}>
          Pour confirmer la suppression, tapez <strong>"SUPPRIMER"</strong> ci-dessous :
        </Typography>
        
        <TextField
          fullWidth
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder="Tapez SUPPRIMER pour confirmer"
          variant="outlined"
          sx={{ mb: 2 }}
          error={confirmationText.length > 0 && !isConfirmationValid}
          helperText={
            confirmationText.length > 0 && !isConfirmationValid
              ? 'Vous devez taper exactement "SUPPRIMER"'
              : ''
          }
          disabled={isConfirming || loading || preparing}
          autoFocus={!preparing}
        />

        {/* Avertissement final */}
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Attention :</strong> Les ordres seront supprimés de la collection principale, 
            mais une copie sera conservée dans l'archive (orders_deleted) avec l'identité de l'administrateur 
            ayant effectué la suppression et la date/heure de l'opération.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isConfirming || loading || preparing}
          sx={{ minWidth: 120 }}
        >
          Annuler
        </Button>
        
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!isConfirmationValid || isConfirming || loading || preparing}
          startIcon={
            (isConfirming || loading) ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
          sx={{ minWidth: 120 }}
        >
          {isConfirming || loading ? 'Suppression...' : 'Supprimer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
